import { useEffect, useMemo, useRef, useState, FormEvent } from 'react';
import { supabase } from '@lib/supabase';
import Button from '@components/Button';
import Modal from '@components/Modal';
import type { BadgeWithCategory, BadgeCategory, Badge } from '@lib/badges';
import { getBadgeIcon, fetchBadgeCategories } from '@lib/badges';

type SortKey =
  | 'name'
  | 'category'
  | 'requirement_type'
  | 'requirement_value'
  | 'score'
  | 'active'
  | 'created_at'
  | 'updated_at';

type CategorySortKey = 'name' | 'created_at' | 'updated_at' | 'active';

function formatDate(value: string | null | undefined): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function formatRequirement(badge: BadgeWithCategory): string {
  if (badge.requirement_type === 'streak_days') {
    return `${badge.requirement_value} day streak`;
  }
  const label = badge.requirement_type.replace(/_/g, ' ').trim();
  return `${badge.requirement_value} ${label}`;
}

function ActionsMenu(props: {
  id?: string;
  label?: string;
  items: Array<{
    key: string;
    label: string;
    onClick: () => void;
    className?: string;
    disabled?: boolean;
  }>;
}) {
  const { label = 'Actions', items } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [ref]);

  return (
    <div ref={ref as any} className="relative inline-block text-left">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        <span className="sr-only">{label}</span>
        <svg
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <circle cx="12" cy="12" r="1" />
          <circle cx="19" cy="12" r="1" />
          <circle cx="5" cy="12" r="1" />
        </svg>
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-2xl border border-gray-100 bg-white shadow-lg">
          <div className="py-2">
            {items.map((it) => (
              <button
                key={it.key}
                type="button"
                onClick={() => {
                  it.onClick();
                  setOpen(false);
                }}
                disabled={it.disabled}
                className={`w-full px-4 py-2 text-left text-sm ${it.className ?? 'text-gray-700'} ${it.disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50'}`}
              >
                {it.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AdminBadges() {
  const [badges, setBadges] = useState<BadgeWithCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'badges' | 'categories'>('badges');
  const [badgeSearch, setBadgeSearch] = useState('');
  const [debouncedBadgeSearch, setDebouncedBadgeSearch] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] =
    useState<string>('');
  const [categorySearch, setCategorySearch] = useState('');
  const [debouncedCategorySearch, setDebouncedCategorySearch] = useState('');
  const [sort, setSort] = useState<{
    key: SortKey | null;
    dir: 'asc' | 'desc' | null;
  }>({
    key: 'requirement_value',
    dir: 'asc',
  });
  const [categorySort, setCategorySort] = useState<{
    key: CategorySortKey | null;
    dir: 'asc' | 'desc' | null;
  }>({
    key: 'name',
    dir: 'asc',
  });
  const [stats, setStats] = useState<{
    total: number | null;
    active: number | null;
    inactive: number | null;
  }>({
    total: null,
    active: null,
    inactive: null,
  });
  const [categoryStats, setCategoryStats] = useState<{
    total: number | null;
  }>({
    total: null,
  });
  const [categories, setCategories] = useState<BadgeCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [editingBadge, setEditingBadge] = useState<BadgeWithCategory | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBadge, setDeletingBadge] = useState<BadgeWithCategory | null>(
    null
  );
  const [applyingBadgeId, setApplyingBadgeId] = useState<string | null>(null);
  const [applyResults, setApplyResults] = useState<Record<string, string>>({});
  const [confirmingBadge, setConfirmingBadge] =
    useState<BadgeWithCategory | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [isDeletingBadge, setIsDeletingBadge] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    image_url: string | null;
    description: string | null;
    requirement_value: number;
    requirement_type: string;
    icon_key: string | null;
    score?: number;
    color: string | null;
    active: boolean;
    category_id: string;
  }>({
    name: '',
    image_url: null,
    description: null,
    requirement_value: 0,
    requirement_type: 'streak_days',
    icon_key: null,
    active: true,
    score: 0,
    color: null,
    category_id: '',
  });

  const [editingCategory, setEditingCategory] = useState<BadgeCategory | null>(
    null
  );
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [showCategoryDeleteModal, setShowCategoryDeleteModal] = useState(false);
  const [deletingCategory, setDeletingCategory] =
    useState<BadgeCategory | null>(null);
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);
  const [categoryDeleteError, setCategoryDeleteError] = useState<string | null>(
    null
  );
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryFormError, setCategoryFormError] = useState<string | null>(
    null
  );
  const [categoryFormData, setCategoryFormData] = useState<{
    name: string;
    description: string | null;
    active?: boolean;
  }>({
    name: '',
    description: null,
  });

  useEffect(() => {
    let isMounted = true;

    const loadBadges = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: queryError } = await supabase
          .from('badges')
          .select(
            `
            *,
            category:badge_categories(*)
          `
          )
          .order('requirement_value', { ascending: true });

        if (queryError) {
          throw queryError;
        }

        if (isMounted) {
          const rows = (data ?? []) as BadgeWithCategory[];
          setBadges(rows);
          const total = rows.length;
          const activeCount = rows.filter((badge) => badge.active).length;
          setStats({
            total,
            active: activeCount,
            inactive: total - activeCount,
          });
        }
      } catch (err) {
        if (isMounted) {
          const message =
            err instanceof Error
              ? err.message
              : String(
                  err ?? 'Unable to load badges right now. Please try again.'
                );
          setError(message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadBadges();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      if (isMounted) setCategoriesLoading(true);
      try {
        const data = await fetchBadgeCategories();
        if (isMounted) {
          setCategories(data);
          setCategoryStats({
            total: data.length,
          });
        }
      } catch (err) {
        console.warn('Failed to fetch badge categories', err);
      } finally {
        if (isMounted) setCategoriesLoading(false);
      }
    };
    loadCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedBadgeSearch(badgeSearch.trim().toLowerCase()),
      300
    );
    return () => clearTimeout(timer);
  }, [badgeSearch]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedCategorySearch(categorySearch.trim().toLowerCase()),
      300
    );
    return () => clearTimeout(timer);
  }, [categorySearch]);

  const filteredBadges = useMemo(() => {
    let list = badges;

    if (selectedCategoryFilter) {
      if (selectedCategoryFilter === 'uncategorized') {
        list = list.filter((b) => !b.category_id);
      } else {
        list = list.filter((b) => b.category_id === selectedCategoryFilter);
      }
    }

    if (!debouncedBadgeSearch) return list;
    return list.filter((badge) => {
      const haystack =
        `${badge.name} ${badge.description ?? ''} ${badge.category?.name ?? ''} ${badge.requirement_type}`.toLowerCase();
      return haystack.includes(debouncedBadgeSearch);
    });
  }, [badges, debouncedBadgeSearch, selectedCategoryFilter]);

  const tableRows = useMemo(() => {
    if (!sort.key || !sort.dir) return filteredBadges;
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    const copy = [...filteredBadges];
    copy.sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      switch (sort.key) {
        case 'category':
          va = a.category?.name ?? '';
          vb = b.category?.name ?? '';
          break;
        case 'requirement_type':
          va = a.requirement_type;
          vb = b.requirement_type;
          break;
        case 'requirement_value':
          va = a.requirement_value;
          vb = b.requirement_value;
          break;
        case 'score':
          va = a.score ?? 0;
          vb = b.score ?? 0;
          break;
        case 'active':
          va = a.active ? 1 : 0;
          vb = b.active ? 1 : 0;
          break;
        case 'created_at':
          va = new Date(a.created_at).getTime();
          vb = new Date(b.created_at).getTime();
          break;
        case 'updated_at':
          va = new Date(a.updated_at).getTime();
          vb = new Date(b.updated_at).getTime();
          break;
        case 'name':
        default:
          va = a.name;
          vb = b.name;
          break;
      }

      let result: number;
      if (typeof va === 'number' && typeof vb === 'number') {
        result = va - vb;
      } else {
        result = collator.compare(String(va), String(vb));
      }
      return sort.dir === 'asc' ? result : -result;
    });
    return copy;
  }, [filteredBadges, sort]);

  const hasData = tableRows.length > 0;
  const filteredCategories = useMemo(() => {
    if (!debouncedCategorySearch) return categories;
    return categories.filter((category) => {
      const haystack =
        `${category.name} ${category.description ?? ''}`.toLowerCase();
      return haystack.includes(debouncedCategorySearch);
    });
  }, [categories, debouncedCategorySearch]);

  const categoryTableRows = useMemo(() => {
    if (!categorySort.key || !categorySort.dir) return filteredCategories;
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    const copy = [...filteredCategories];
    copy.sort((a, b) => {
      let result = 0;
      switch (categorySort.key) {
        case 'active': {
          const va = a.active ? 1 : 0;
          const vb = b.active ? 1 : 0;
          result = va - vb;
          break;
        }
        case 'created_at':
        case 'updated_at': {
          const da = new Date(a[categorySort.key]).getTime();
          const db = new Date(b[categorySort.key]).getTime();
          result = da - db;
          break;
        }
        case 'name':
        default:
          result = collator.compare(a.name, b.name);
          break;
      }
      return categorySort.dir === 'asc' ? result : -result;
    });
    return copy;
  }, [filteredCategories, categorySort]);

  const categoryHasData = categoryTableRows.length > 0;
  const selectedCategoryMissing =
    !!formData.category_id &&
    !categories.some((category) => category.id === formData.category_id);

  function toggleSort(key: SortKey) {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: null };
    });
  }

  function toggleCategorySort(key: CategorySortKey) {
    setCategorySort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: null };
    });
  }

  const openAddBadge = () => {
    setEditingBadge(null);
    setFormError(null);
    setFormData({
      name: '',
      image_url: null,
      description: null,
      requirement_value: 0,
      requirement_type: 'streak_days',
      icon_key: null,
      active: true,
      score: 0,
      category_id: categories[0]?.id ?? '',
      color: null,
    });
    setIsModalOpen(true);
  };

  const openAddCategory = () => {
    setEditingCategory(null);
    setCategoryFormError(null);
    setCategoryFormData({ name: '', description: null, active: true });
    setIsCategoryModalOpen(true);
  };

  const handleEditBadge = (badge: BadgeWithCategory) => {
    setEditingBadge(badge);
    setFormError(null);
    setFormData({
      name: badge.name,
      image_url: badge.image_url ?? null,
      description: badge.description,
      requirement_value: badge.requirement_value,
      requirement_type: badge.requirement_type,
      icon_key: badge.icon_key,
      color: badge.color ?? null,
      active: badge.active,
      score: badge.score ?? 0,
      category_id: badge.category_id,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
    setEditingBadge(null);
  };

  const openDeleteModal = (badge: BadgeWithCategory) => {
    setDeletingBadge(badge);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (isDeletingBadge) return;
    setShowDeleteModal(false);
    setDeletingBadge(null);
    setDeleteError(null);
  };

  const handleDeleteBadge = async () => {
    if (!deletingBadge) return;
    setIsDeletingBadge(true);
    setDeleteError(null);
    try {
      const { error } = await supabase
        .from('badges')
        .delete()
        .eq('id', deletingBadge.id);
      if (error) throw error;
      setBadges((prev) => prev.filter((b) => b.id !== deletingBadge.id));
      // update stats
      setStats((prev) => {
        const total = (prev.total ?? 0) - 1;
        const active = (prev.active ?? 0) - (deletingBadge.active ? 1 : 0);
        return { total, active, inactive: total - active };
      });
      setShowDeleteModal(false);
      setDeletingBadge(null);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : String(err ?? 'Failed to delete badge.');
      setDeleteError(message);
    } finally {
      setIsDeletingBadge(false);
    }
  };

  const handleInputChange = (
    field: keyof typeof formData,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveBadge = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      if (editingBadge) {
        const updates = {
          name: formData.name.trim(),
          image_url: formData.image_url?.trim() || null,
          description: formData.description?.trim() || null,
          requirement_value: formData.requirement_value,
          icon_key: formData.icon_key?.trim() || null,
          active: formData.active,
          category_id: formData.category_id,
          color: formData.color?.trim() || null,
          score: Number(formData.score ?? 0),
          updated_at: new Date().toISOString(),
        };

        const { data: updatedRows, error: updateError } = await supabase
          .from('badges')
          .update(updates)
          .eq('id', editingBadge.id)
          .select('*')
          .maybeSingle();

        if (updateError) {
          throw updateError;
        }

        if (!updatedRows) {
          // No row returned — could be due to RLS or the row not existing.
          throw new Error('No badge returned from update (check permissions).');
        }

        setBadges((prev) => {
          const updated = prev.map((badge) =>
            badge.id === editingBadge.id
              ? {
                  ...badge,
                  ...(updatedRows || {}),
                  category:
                    categories.find((cat) => cat.id === updates.category_id) ||
                    badge.category,
                }
              : badge
          );
          const total = updated.length;
          const activeCount = updated.filter((b) => b.active).length;
          setStats({
            total,
            active: activeCount,
            inactive: total - activeCount,
          });
          return updated;
        });

        setIsModalOpen(false);
        setEditingBadge(null);
      } else {
        // Create new badge
        const payload: Partial<Badge> & {
          created_at: string;
          updated_at: string;
        } = {
          name: formData.name.trim(),
          image_url: formData.image_url?.trim() || null,
          description: formData.description?.trim() || null,
          requirement_value: formData.requirement_value,
          requirement_type: formData.requirement_type,
          icon_key: formData.icon_key?.trim() || null,
          score: Number(formData.score ?? 0),
          color: formData.color?.trim() || null,
          active: formData.active,
          category_id: formData.category_id || undefined,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        const { data: newRow, error: insertError } = await supabase
          .from('badges')
          .insert(payload)
          .select('*')
          .maybeSingle();

        if (insertError) throw insertError;

        if (!newRow) throw new Error('No badge returned from insert.');

        // attach category object if available
        const rowWithCategory: BadgeWithCategory = {
          ...(newRow as Badge),
          category:
            categories.find((c) => c.id === (newRow as Badge).category_id) ??
            undefined,
        };

        setBadges((prev) => {
          const next = [rowWithCategory, ...prev];
          const total = next.length;
          const activeCount = next.filter((b) => b.active).length;
          setStats({
            total,
            active: activeCount,
            inactive: total - activeCount,
          });
          return next;
        });

        setIsModalOpen(false);
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : String(err ?? 'Failed to update badge.');
      setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditCategory = (category: BadgeCategory) => {
    setEditingCategory(category);
    setCategoryFormError(null);
    setCategoryFormData({
      name: category.name,
      description: category.description,
      active: category.active ?? true,
    });
    setIsCategoryModalOpen(true);
  };

  const openDeleteCategoryModal = (category: BadgeCategory) => {
    setDeletingCategory(category);
    setCategoryDeleteError(null);
    setShowCategoryDeleteModal(true);
  };

  const closeDeleteCategoryModal = () => {
    if (isDeletingCategory) return;
    setShowCategoryDeleteModal(false);
    setDeletingCategory(null);
    setCategoryDeleteError(null);
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategory) return;
    setIsDeletingCategory(true);
    setCategoryDeleteError(null);
    try {
      const { error } = await supabase
        .from('badge_categories')
        .delete()
        .eq('id', deletingCategory.id);
      if (error) throw error;

      setCategories((prev) => prev.filter((c) => c.id !== deletingCategory.id));
      setCategoryStats((prev) => ({ total: (prev.total ?? 1) - 1 }));

      // detach category from any badges in-memory
      setBadges((prev) =>
        prev.map((b) =>
          b.category_id === deletingCategory.id
            ? { ...b, category: undefined }
            : b
        )
      );

      setShowCategoryDeleteModal(false);
      setDeletingCategory(null);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : String(err ?? 'Failed to delete category.');
      setCategoryDeleteError(message);
    } finally {
      setIsDeletingCategory(false);
    }
  };

  const closeCategoryModal = () => {
    if (categorySaving) return;
    setIsCategoryModalOpen(false);
    setEditingCategory(null);
  };

  const handleCategoryInputChange = (
    field: keyof typeof categoryFormData,
    value: string | boolean
  ) => {
    setCategoryFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveCategory = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCategorySaving(true);
    setCategoryFormError(null);
    try {
      if (editingCategory) {
        const updates = {
          name: categoryFormData.name.trim(),
          description: categoryFormData.description?.trim() || null,
          updated_at: new Date().toISOString(),
        };

        const { error: updateError } = await supabase
          .from('badge_categories')
          .update(updates)
          .eq('id', editingCategory.id);

        if (updateError) throw updateError;

        const updatedCategory: BadgeCategory = {
          ...editingCategory,
          ...updates,
        };

        setCategories((prev) => {
          const next = prev.map((category) =>
            category.id === editingCategory.id ? updatedCategory : category
          );
          setCategoryStats({ total: next.length });
          return next;
        });

        setBadges((prev) =>
          prev.map((badge) =>
            badge.category_id === editingCategory.id
              ? {
                  ...badge,
                  category: {
                    ...(badge.category || updatedCategory),
                    ...updatedCategory,
                  },
                }
              : badge
          )
        );

        setIsCategoryModalOpen(false);
        setEditingCategory(null);
      } else {
        // Create new category
        const payload: Partial<BadgeCategory> & {
          created_at: string;
          updated_at: string;
        } = {
          name: categoryFormData.name.trim(),
          description: categoryFormData.description?.trim() || null,
          active: categoryFormData?.active ?? true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        const { data: newRow, error: insertError } = await supabase
          .from('badge_categories')
          .insert(payload)
          .select('*')
          .maybeSingle();

        if (insertError) throw insertError;
        if (!newRow) throw new Error('No category returned from insert.');

        const created: BadgeCategory = { ...(newRow as BadgeCategory) };

        setCategories((prev) => {
          const next = [created, ...prev];
          setCategoryStats({ total: next.length });
          return next;
        });

        setIsCategoryModalOpen(false);
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : String(err ?? 'Failed to update category.');
      setCategoryFormError(message);
    } finally {
      setCategorySaving(false);
    }
  };

  const renderBadgesTab = () => (
    <>
      <div className="mb-4 flex flex-col items-center justify-center gap-4 sm:flex-row">
        <div className="w-full max-w-2xl flex-1">
          <label htmlFor="admin-badge-search" className="sr-only">
            Search badges
          </label>
          <div className="relative rounded-lg border border-gray-200 bg-white/60 px-3 py-2 shadow-sm">
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                d="M21 21l-4.35-4.35"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="11"
                cy="11"
                r="6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              id="admin-badge-search"
              type="search"
              value={badgeSearch}
              onChange={(e) => setBadgeSearch(e.target.value)}
              placeholder="Search badges by name, description, or category"
              className="w-full border-0 bg-transparent pl-9 text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
            />
            {badgeSearch && (
              <button
                onClick={() => setBadgeSearch('')}
                type="button"
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
              >
                clear
              </button>
            )}
          </div>
        </div>
        <div className="w-full max-w-xs sm:w-auto">
          <label htmlFor="filter-category" className="sr-only">
            Filter by category
          </label>
          <select
            id="filter-category"
            value={selectedCategoryFilter}
            onChange={(e) => setSelectedCategoryFilter(e.target.value)}
            className="mt-2 w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:mt-0"
          >
            <option value="">All categories</option>
            <option value="uncategorized">Uncategorized</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className="h-24 animate-pulse rounded-2xl bg-white/60 backdrop-blur"
            />
          ))}
        </div>
      ) : !hasData ? (
        <div className="rounded-2xl border border-dashed border-teal-200 bg-white/60 px-6 py-12 text-center text-gray-600">
          No badges found.
        </div>
      ) : (
        <>
          <div className="space-y-4 lg:hidden">
            {tableRows.map((badge) => (
              <div
                key={badge.id}
                className="rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-sm"
              >
                <div className="flex items-center gap-3 text-lg font-semibold text-gray-900">
                  {badge.image_url ? (
                    <img
                      src={badge.image_url}
                      alt={badge.name}
                      className="h-10 w-10 cursor-zoom-in rounded-md object-cover"
                      onClick={() =>
                        setPreviewImageUrl(badge.image_url ?? null)
                      }
                    />
                  ) : (
                    <span className="text-2xl">{getBadgeIcon(badge)}</span>
                  )}
                  {badge.name}
                </div>
                <div className="mt-4">
                  <label
                    htmlFor="badge-score"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Score
                  </label>
                  <input
                    id="badge-score"
                    type="number"
                    min={0}
                    value={formData.score ?? 0}
                    onChange={(e) =>
                      handleInputChange('score', Number(e.target.value))
                    }
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 text-sm text-gray-700">
                  <div>
                    <p className="font-medium text-gray-500">Category</p>
                    <p>{badge.category?.name ?? 'Uncategorized'}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-500">
                      Requirement Type
                    </p>
                    <p className="text-xs uppercase tracking-wide text-gray-500">
                      {badge.requirement_type}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-500">Requirement</p>
                    <p>{formatRequirement(badge)}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-500">Score</p>
                    <p>{badge.score ?? 0}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-500">Status</p>
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        badge.active
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {badge.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-xs uppercase tracking-wide text-gray-500">
                    <span>Created {formatDate(badge.created_at)}</span>
                    <span>Updated {formatDate(badge.updated_at)}</span>
                  </div>
                  {badge.description ? (
                    <div className="col-span-full">
                      <p className="font-medium text-gray-500">Description</p>
                      <p>{badge.description}</p>
                    </div>
                  ) : null}
                  <div>
                    <p className="font-medium text-gray-500">Actions</p>
                    <div>
                      <ActionsMenu
                        id={badge.id}
                        items={[
                          {
                            key: 'edit',
                            label: 'Edit',
                            onClick: () => handleEditBadge(badge),
                            className: '!text-indigo-700',
                          },
                          {
                            key: 'delete',
                            label: 'Delete',
                            onClick: () => openDeleteModal(badge),
                            className: '!text-rose-700',
                          },
                          {
                            key: 'apply',
                            label:
                              applyingBadgeId === badge.id
                                ? 'Applying…'
                                : 'Apply to current users',
                            onClick: () => setConfirmingBadge(badge),
                            className: '!text-orange-700',
                            disabled: applyingBadgeId === badge.id,
                          },
                        ]}
                      />
                      {applyResults[badge.id] ? (
                        <div className="mt-2 text-xs text-gray-600">
                          {applyResults[badge.id]}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden lg:block">
            <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
              <table className="min-w-full text-left text-sm text-gray-800">
                <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Image</th>
                    <th className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleSort('name')}
                        className="flex items-center gap-2"
                        aria-label="Sort by name"
                      >
                        <span>Name</span>
                        <span className="text-xs text-gray-400">
                          {sort.key === 'name'
                            ? sort.dir === 'asc'
                              ? '▲'
                              : sort.dir === 'desc'
                                ? '▼'
                                : ''
                            : ''}
                        </span>
                      </button>
                    </th>
                    <th className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleSort('score')}
                        className="flex items-center gap-2"
                        aria-label="Sort by score"
                      >
                        <span>Score</span>
                        <span className="text-xs text-gray-400">
                          {sort.key === 'score'
                            ? sort.dir === 'asc'
                              ? '▲'
                              : sort.dir === 'desc'
                                ? '▼'
                                : ''
                            : ''}
                        </span>
                      </button>
                    </th>
                    <th className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleSort('category')}
                        className="flex items-center gap-2"
                        aria-label="Sort by category"
                      >
                        <span>Category</span>
                        <span className="text-xs text-gray-400">
                          {sort.key === 'category'
                            ? sort.dir === 'asc'
                              ? '▲'
                              : sort.dir === 'desc'
                                ? '▼'
                                : ''
                            : ''}
                        </span>
                      </button>
                    </th>
                    <th className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleSort('requirement_type')}
                        className="flex items-center gap-2"
                        aria-label="Sort by requirement type"
                      >
                        <span>Requirement Type</span>
                        <span className="text-xs text-gray-400">
                          {sort.key === 'requirement_type'
                            ? sort.dir === 'asc'
                              ? '▲'
                              : sort.dir === 'desc'
                                ? '▼'
                                : ''
                            : ''}
                        </span>
                      </button>
                    </th>
                    <th className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleSort('requirement_value')}
                        className="flex items-center gap-2"
                        aria-label="Sort by requirement value"
                      >
                        <span>Requirement</span>
                        <span className="text-xs text-gray-400">
                          {sort.key === 'requirement_value'
                            ? sort.dir === 'asc'
                              ? '▲'
                              : sort.dir === 'desc'
                                ? '▼'
                                : ''
                            : ''}
                        </span>
                      </button>
                    </th>
                    <th className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleSort('active')}
                        className="flex items-center gap-2"
                        aria-label="Sort by status"
                      >
                        <span>Status</span>
                        <span className="text-xs text-gray-400">
                          {sort.key === 'active'
                            ? sort.dir === 'asc'
                              ? '▲'
                              : sort.dir === 'desc'
                                ? '▼'
                                : ''
                            : ''}
                        </span>
                      </button>
                    </th>
                    <th className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleSort('created_at')}
                        className="flex items-center gap-2"
                        aria-label="Sort by created date"
                      >
                        <span>Created</span>
                        <span className="text-xs text-gray-400">
                          {sort.key === 'created_at'
                            ? sort.dir === 'asc'
                              ? '▲'
                              : sort.dir === 'desc'
                                ? '▼'
                                : ''
                            : ''}
                        </span>
                      </button>
                    </th>
                    <th className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleSort('updated_at')}
                        className="flex items-center gap-2"
                        aria-label="Sort by updated date"
                      >
                        <span>Updated</span>
                        <span className="text-xs text-gray-400">
                          {sort.key === 'updated_at'
                            ? sort.dir === 'asc'
                              ? '▲'
                              : sort.dir === 'desc'
                                ? '▼'
                                : ''
                            : ''}
                        </span>
                      </button>
                    </th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((badge) => (
                    <tr
                      key={badge.id}
                      className="border-t border-gray-100 hover:bg-indigo-50"
                    >
                      <td className="px-4 py-4 align-top text-gray-900">
                        {badge.image_url ? (
                          <img
                            src={badge.image_url}
                            alt={badge.name}
                            className="h-10 w-10 cursor-zoom-in rounded-md object-cover"
                            onClick={() =>
                              setPreviewImageUrl(badge.image_url ?? null)
                            }
                          />
                        ) : (
                          <span className="text-2xl">
                            {getBadgeIcon(badge)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 align-top text-gray-900">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-3">
                            <span className="font-semibold">{badge.name}</span>
                          </div>
                          {badge.description ? (
                            <span className="max-w-md text-sm text-gray-600">
                              {badge.description}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top text-gray-900">
                        <div className="font-semibold">{badge.score ?? 0}</div>
                      </td>
                      <td className="px-4 py-4 align-top text-gray-900">
                        {badge.category?.name ?? 'Uncategorized'}
                      </td>
                      <td className="px-4 py-4 align-top text-gray-900">
                        <div className="text-xs uppercase tracking-wide text-gray-500">
                          {badge.requirement_type}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top text-gray-900">
                        {formatRequirement(badge)}
                      </td>
                      <td className="px-4 py-4 align-top text-gray-900">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                            badge.active
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {badge.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-top text-gray-900">
                        {formatDate(badge.created_at)}
                      </td>
                      <td className="px-4 py-4 align-top text-gray-900">
                        {formatDate(badge.updated_at)}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex flex-wrap justify-end gap-3">
                          <ActionsMenu
                            id={badge.id}
                            items={[
                              {
                                key: 'edit',
                                label: 'Edit',
                                onClick: () => handleEditBadge(badge),
                                className: '!text-indigo-700',
                              },
                              {
                                key: 'delete',
                                label: 'Delete',
                                onClick: () => openDeleteModal(badge),
                                className: '!text-rose-700',
                              },
                              {
                                key: 'apply',
                                label:
                                  applyingBadgeId === badge.id
                                    ? 'Applying…'
                                    : 'Apply to current users',
                                onClick: () => setConfirmingBadge(badge),
                                className: '!text-orange-700',
                                disabled: applyingBadgeId === badge.id,
                              },
                            ]}
                          />
                          {applyResults[badge.id] ? (
                            <div className="mt-2 text-right text-xs text-gray-600 lg:hidden">
                              {applyResults[badge.id]}
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );

  const renderCategoriesTab = () => (
    <>
      <div className="mb-4 flex flex-col items-center justify-center gap-4 sm:flex-row">
        <div className="w-full max-w-xl flex-1">
          <label htmlFor="admin-category-search" className="sr-only">
            Search categories
          </label>
          <div className="relative rounded-lg border border-gray-200 bg-white/60 px-3 py-2 shadow-sm">
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                d="M21 21l-4.35-4.35"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="11"
                cy="11"
                r="6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              id="admin-category-search"
              type="search"
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              placeholder="Search categories by name or description"
              className="w-full border-0 bg-transparent pl-9 text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
            />
            {categorySearch && (
              <button
                onClick={() => setCategorySearch('')}
                type="button"
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
              >
                clear
              </button>
            )}
          </div>
        </div>
        <div className="w-full max-w-xs sm:w-auto" />
      </div>

      {categoriesLoading ? (
        <div className="space-y-4">
          {[0, 1].map((index) => (
            <div
              key={index}
              className="h-20 animate-pulse rounded-2xl bg-white/60 backdrop-blur"
            />
          ))}
        </div>
      ) : !categoryHasData ? (
        <div className="rounded-2xl border border-dashed border-teal-200 bg-white/60 px-6 py-12 text-center text-gray-600">
          No badge categories found.
        </div>
      ) : (
        <>
          <div className="space-y-4 lg:hidden">
            {categoryTableRows.map((category) => (
              <div
                key={category.id}
                className="rounded-2xl border border-gray-100 bg-white/90 p-5 shadow-sm"
              >
                <div className="text-lg font-semibold text-gray-900">
                  {category.name}
                </div>
                <div className="mt-3 grid gap-4 text-sm text-gray-700">
                  <div>
                    <p className="font-medium text-gray-500">Description</p>
                    <p>{category.description || '—'}</p>
                  </div>
                  <div className="flex gap-6 text-xs uppercase tracking-wide text-gray-500">
                    <span>Created {formatDate(category.created_at)}</span>
                    <span>Updated {formatDate(category.updated_at)}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-500">Status</p>
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        category.active
                          ? 'bg-emerald-50 text-emerald-700'
                          : category.active === false
                            ? 'bg-gray-100 text-gray-500'
                            : 'bg-gray-50 text-gray-400'
                      }`}
                    >
                      {category.active === undefined
                        ? '—'
                        : category.active
                          ? 'Active'
                          : 'Inactive'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-500">Actions</p>
                    <div className="mt-2">
                      <ActionsMenu
                        id={category.id}
                        items={[
                          {
                            key: 'edit',
                            label: 'Edit',
                            onClick: () => handleEditCategory(category),
                            className: '!text-indigo-700',
                          },
                          {
                            key: 'delete',
                            label: 'Delete',
                            onClick: () => openDeleteCategoryModal(category),
                            className: '!text-rose-700',
                          },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden lg:block">
            <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
              <table className="min-w-full text-left text-sm text-gray-800">
                <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleCategorySort('name')}
                        className="flex items-center gap-2"
                        aria-label="Sort by name"
                      >
                        <span>Name</span>
                        <span className="text-xs text-gray-400">
                          {categorySort.key === 'name'
                            ? categorySort.dir === 'asc'
                              ? '▲'
                              : categorySort.dir === 'desc'
                                ? '▼'
                                : ''
                            : ''}
                        </span>
                      </button>
                    </th>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleCategorySort('active')}
                        className="flex items-center gap-2"
                        aria-label="Sort by status"
                      >
                        <span>Status</span>
                        <span className="text-xs text-gray-400">
                          {categorySort.key === 'active'
                            ? categorySort.dir === 'asc'
                              ? '▲'
                              : categorySort.dir === 'desc'
                                ? '▼'
                                : ''
                            : ''}
                        </span>
                      </button>
                    </th>
                    <th className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleCategorySort('created_at')}
                        className="flex items-center gap-2"
                        aria-label="Sort by created date"
                      >
                        <span>Created</span>
                        <span className="text-xs text-gray-400">
                          {categorySort.key === 'created_at'
                            ? categorySort.dir === 'asc'
                              ? '▲'
                              : categorySort.dir === 'desc'
                                ? '▼'
                                : ''
                            : ''}
                        </span>
                      </button>
                    </th>
                    <th className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleCategorySort('updated_at')}
                        className="flex items-center gap-2"
                        aria-label="Sort by updated date"
                      >
                        <span>Updated</span>
                        <span className="text-xs text-gray-400">
                          {categorySort.key === 'updated_at'
                            ? categorySort.dir === 'asc'
                              ? '▲'
                              : categorySort.dir === 'desc'
                                ? '▼'
                                : ''
                            : ''}
                        </span>
                      </button>
                    </th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryTableRows.map((category) => (
                    <tr
                      key={category.id}
                      className="border-t border-gray-100 hover:bg-indigo-50"
                    >
                      <td className="px-4 py-4 align-top text-gray-900">
                        {category.name}
                      </td>
                      <td className="px-4 py-4 align-top text-gray-900">
                        <span className="max-w-lg text-sm text-gray-600">
                          {category.description ?? '—'}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-top text-gray-900">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                            category.active
                              ? 'bg-emerald-50 text-emerald-700'
                              : category.active === false
                                ? 'bg-gray-100 text-gray-500'
                                : 'bg-gray-50 text-gray-400'
                          }`}
                        >
                          {category.active === undefined
                            ? '—'
                            : category.active
                              ? 'Active'
                              : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-top text-gray-900">
                        {formatDate(category.created_at)}
                      </td>
                      <td className="px-4 py-4 align-top text-gray-900">
                        {formatDate(category.updated_at)}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex flex-wrap justify-end gap-3">
                          <ActionsMenu
                            id={category.id}
                            items={[
                              {
                                key: 'edit',
                                label: 'Edit',
                                onClick: () => handleEditCategory(category),
                                className: '!text-indigo-700',
                              },
                              {
                                key: 'delete',
                                label: 'Delete',
                                onClick: () =>
                                  openDeleteCategoryModal(category),
                                className: '!text-rose-700',
                              },
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100">
      <div
        className="absolute inset-0 animate-pulse bg-gradient-to-br from-teal-200/30 via-blue-200/20 to-cyan-200/30"
        style={{ animationDuration: '8s' }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-4 py-12">
        <div className="mb-6 text-center">
          <h1 className="gradient-text mb-4 pb-2 text-4xl font-bold sm:text-5xl">
            Admin · Badges
          </h1>
          <p className="text-lg text-gray-600">
            Review and audit the badges available for the community.
          </p>
          {activeTab === 'badges' ? (
            <div className="mt-4 flex flex-wrap justify-center gap-6 text-sm text-gray-600">
              <div>
                <span className="font-semibold text-gray-900">
                  {typeof stats.total === 'number' ? stats.total : '--'}
                </span>{' '}
                total
              </div>
              <div>
                <span className="font-semibold text-gray-900">
                  {typeof stats.active === 'number' ? stats.active : '--'}
                </span>{' '}
                active
              </div>
              <div>
                <span className="font-semibold text-gray-900">
                  {typeof stats.inactive === 'number' ? stats.inactive : '--'}
                </span>{' '}
                inactive
              </div>
            </div>
          ) : (
            <div className="mt-4 flex flex-wrap justify-center gap-6 text-sm text-gray-600">
              <div>
                <span className="font-semibold text-gray-900">
                  {typeof categoryStats.total === 'number'
                    ? categoryStats.total
                    : '--'}
                </span>{' '}
                categories
              </div>
            </div>
          )}
        </div>

        <div className="animate-fade-in rounded-3xl bg-white/70 p-8 shadow-xl backdrop-blur">
          <div className="mb-4 flex justify-start">
            <Button
              type="button"
              onClick={activeTab === 'badges' ? openAddBadge : openAddCategory}
              className="item-selected inline-flex items-center rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              {activeTab === 'badges' ? 'Add Badge' : 'Add Category'}
            </Button>
          </div>

          <div className="mt-8 flex justify-center">
            <div className="inline-flex gap-3 rounded-full bg-gray-100 p-1">
              {(
                [
                  { key: 'badges', label: 'Badges' },
                  { key: 'categories', label: 'Badge Categories' },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
                    activeTab === tab.key
                      ? 'item-selected bg-white text-gray-900 shadow'
                      : 'bg-transparent text-gray-500 hover:bg-white hover:text-gray-800'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          <div className="mt-6">
            {activeTab === 'badges' ? renderBadgesTab() : renderCategoriesTab()}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative flex max-h-[90vh] w-full max-w-xl flex-col rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={closeModal}
              className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
              aria-label="Close edit badge modal"
              disabled={saving}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">
              {editingBadge ? 'Edit Badge' : 'Add Badge'}
            </h2>
            <p className="text-sm text-gray-500">
              {editingBadge ? (
                <>
                  Update badge details and requirements for{' '}
                  <span className="font-semibold text-gray-900">
                    {editingBadge.name}
                  </span>
                  .
                </>
              ) : (
                <>
                  Create a new badge for the community and define its
                  requirements.
                </>
              )}
            </p>
            {formError && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {formError}
              </div>
            )}
            <form
              className="mt-6 flex min-h-0 flex-1 flex-col"
              onSubmit={handleSaveBadge}
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-2">
                <div>
                  <label
                    htmlFor="badge-name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Badge Name
                  </label>
                  <input
                    id="badge-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="badge-description"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Description
                  </label>
                  <textarea
                    id="badge-description"
                    value={formData.description ?? ''}
                    onChange={(e) =>
                      handleInputChange('description', e.target.value)
                    }
                    rows={3}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="badge-image-url"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Badge Image URL
                  </label>
                  <input
                    id="badge-image-url"
                    type="url"
                    value={formData.image_url ?? ''}
                    onChange={(e) =>
                      handleInputChange('image_url', e.target.value)
                    }
                    placeholder="https://example.com/badges/1.png"
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {formData.image_url ? (
                    <div className="mt-3">
                      <img
                        src={formData.image_url}
                        alt="preview"
                        className="h-16 w-16 rounded-md object-cover"
                      />
                    </div>
                  ) : null}
                </div>
                <div>
                  <label
                    htmlFor="badge-color"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Color
                  </label>
                  <div className="mt-1 flex items-center gap-3">
                    <input
                      id="badge-color"
                      type="text"
                      value={formData.color ?? ''}
                      onChange={(e) =>
                        handleInputChange('color', e.target.value)
                      }
                      placeholder="#FFCC00"
                      className="w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {formData.color ? (
                      <div
                        aria-hidden
                        className="h-8 w-8 rounded-lg border"
                        style={{ background: formData.color }}
                      />
                    ) : null}
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="badge-category"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Category
                  </label>
                  <select
                    id="badge-category"
                    value={formData.category_id}
                    onChange={(e) =>
                      handleInputChange('category_id', e.target.value)
                    }
                    required
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="" disabled>
                      Select category
                    </option>
                    {selectedCategoryMissing && formData.category_id && (
                      <option value={formData.category_id}>
                        {editingBadge?.category?.name || 'Current category'}
                      </option>
                    )}
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <label
                      htmlFor="badge-requirement-type"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Requirement Type
                    </label>
                    <input
                      id="badge-requirement-type"
                      type="text"
                      value={formData.requirement_type}
                      disabled
                      className="mt-1 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="badge-score"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Score
                    </label>
                    <input
                      id="badge-score"
                      type="number"
                      min={0}
                      value={formData.score ?? 0}
                      onChange={(e) =>
                        handleInputChange('score', Number(e.target.value))
                      }
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="badge-requirement-value"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Requirement Value
                    </label>
                    <input
                      id="badge-requirement-value"
                      type="number"
                      min={0}
                      value={formData.requirement_value}
                      onChange={(e) =>
                        handleInputChange(
                          'requirement_value',
                          Number(e.target.value)
                        )
                      }
                      required
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="badge-icon"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Icon (emoji or key)
                    </label>
                    <input
                      id="badge-icon"
                      type="text"
                      value={formData.icon_key ?? ''}
                      onChange={(e) =>
                        handleInputChange('icon_key', e.target.value)
                      }
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      id="badge-active"
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) =>
                        handleInputChange('active', e.target.checked)
                      }
                      className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label
                      htmlFor="badge-active"
                      className="text-sm text-gray-700"
                    >
                      Active
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex flex-shrink-0 justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="item-selected rounded-full bg-indigo-600 px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmingBadge && (
        <Modal
          title={`Apply badge — ${confirmingBadge.name}`}
          onClose={() => setConfirmingBadge(null)}
        >
          <p className="text-sm text-gray-700">
            Apply this badge to all existing users who meet its requirements?
          </p>
          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" onClick={() => setConfirmingBadge(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="!bg-orange-500 !text-white hover:!bg-orange-600 focus:!ring-orange-400"
              onClick={async () => {
                if (!confirmingBadge) return;
                const badge = confirmingBadge;
                setConfirming(true);
                setApplyResults((prev) => ({ ...prev, [badge.id]: '' }));
                setApplyingBadgeId(badge.id);
                try {
                  const res = await fetch('/api/admin-apply-badge', {
                    method: 'POST',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({ badge_id: badge.id }),
                  });
                  const data = await res.json();
                  if (!res.ok) throw new Error(data?.error || 'Failed');
                  setApplyResults((prev) => ({
                    ...prev,
                    [badge.id]: `Applied to ${data.inserted ?? 0} users (matched ${data.matched ?? 0})`,
                  }));
                } catch (err) {
                  const message =
                    err instanceof Error
                      ? err.message
                      : String(err ?? 'Request failed');
                  setApplyResults((prev) => ({
                    ...prev,
                    [badge.id]: `Error: ${message}`,
                  }));
                } finally {
                  setConfirming(false);
                  setApplyingBadgeId(null);
                  setConfirmingBadge(null);
                }
              }}
              disabled={confirming}
            >
              {confirming ? 'Applying…' : 'Apply to current users'}
            </Button>
          </div>
        </Modal>
      )}

      {previewImageUrl && (
        <Modal title="Badge image" onClose={() => setPreviewImageUrl(null)}>
          <div className="flex w-full justify-center">
            <img
              src={previewImageUrl}
              alt="badge preview"
              className="max-h-[80vh] max-w-full rounded-lg object-contain"
            />
          </div>
        </Modal>
      )}

      {isCategoryModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative flex max-h-[85vh] w-full max-w-lg flex-col rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={closeCategoryModal}
              className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
              aria-label="Close edit category modal"
              disabled={categorySaving}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">
              {editingCategory ? 'Edit Category' : 'Add Category'}
            </h2>
            <p className="text-sm text-gray-500">
              {editingCategory ? (
                <>
                  Update category details for{' '}
                  <span className="font-semibold text-gray-900">
                    {editingCategory.name}
                  </span>
                  .
                </>
              ) : (
                <>Create a new category to organize badges.</>
              )}
            </p>
            {categoryFormError && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {categoryFormError}
              </div>
            )}
            <form
              className="mt-6 flex min-h-0 flex-1 flex-col"
              onSubmit={handleSaveCategory}
            >
              <div
                className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-2"
                style={{ maxHeight: '70vh' }}
              >
                <div>
                  <label
                    htmlFor="category-name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Category Name
                  </label>
                  <input
                    id="category-name"
                    type="text"
                    value={categoryFormData.name}
                    onChange={(e) =>
                      handleCategoryInputChange('name', e.target.value)
                    }
                    required
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="category-description"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Description
                  </label>
                  <textarea
                    id="category-description"
                    rows={3}
                    value={categoryFormData.description ?? ''}
                    onChange={(e) =>
                      handleCategoryInputChange('description', e.target.value)
                    }
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    id="category-active"
                    type="checkbox"
                    checked={!!categoryFormData.active}
                    onChange={(e) =>
                      handleCategoryInputChange('active', e.target.checked)
                    }
                    className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label
                    htmlFor="category-active"
                    className="text-sm text-gray-700"
                  >
                    Active
                  </label>
                </div>
              </div>
              <div className="flex flex-shrink-0 justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100"
                  onClick={closeCategoryModal}
                  disabled={categorySaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={categorySaving}
                  className="item-selected rounded-full bg-indigo-600 px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {categorySaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && deletingBadge && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={closeDeleteModal}
              className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
              aria-label="Close delete badge modal"
              disabled={isDeletingBadge}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">Delete Badge</h2>
            <p className="text-sm text-gray-500">
              Are you sure you want to permanently delete the badge{' '}
              <span className="font-semibold text-gray-900">
                {deletingBadge.name}
              </span>
              ? This action cannot be undone.
            </p>
            {deleteError && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {deleteError}
              </div>
            )}
            <div className="mt-6 flex items-center justify-end gap-3">
              <Button type="button" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleDeleteBadge}
                disabled={isDeletingBadge}
                className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
              >
                {isDeletingBadge ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showCategoryDeleteModal && deletingCategory && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={closeDeleteCategoryModal}
              className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-600 transition hover:bg-gray-200"
              aria-label="Close delete category modal"
              disabled={isDeletingCategory}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">
              Delete Category
            </h2>
            <p className="text-sm text-gray-500">
              Are you sure you want to permanently delete the category{' '}
              <span className="font-semibold text-gray-900">
                {deletingCategory.name}
              </span>
              ? Badges in this category will be left uncategorized. This action
              cannot be undone.
            </p>
            {categoryDeleteError && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {categoryDeleteError}
              </div>
            )}
            <div className="mt-6 flex items-center justify-end gap-3">
              <Button type="button" onClick={closeDeleteCategoryModal}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleDeleteCategory}
                disabled={isDeletingCategory}
                className="!bg-rose-500 !text-white hover:!bg-rose-600 focus:!ring-rose-400"
              >
                {isDeletingCategory ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminBadges;
